import type { ApolloError } from '@apollo/client';
import type { GetSessionDataQuery } from '@predium/client-graphql';
import { role_enum } from '@predium/enums';
import { useSelector } from '@xstate/store-react';
import isNil from 'lodash/isNil';
import { ServerSideFlags, sessionStore } from './session.store';

export type Features = 'energySupplier' | 'energyNetwork';

type SessionUser = GetSessionDataQuery['getSessionData']['user'];
type SessionOrg = GetSessionDataQuery['getSessionData']['org'];

type AuthenticatedSession = {
  status: 'authenticated';
  user: SessionUser;
  org: SessionOrg;
  featureFlags: ServerSideFlags;
  isAdmin: boolean;
  hasPortfolioWithWriteAccess: boolean;
  firstBuildingId: number | null;
  updateFeatureFlags: (flags: Partial<ServerSideFlags>) => void;
  hasFeatureWriteAccess: (feature: Features) => boolean;
};

type UseSessionResult =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'error'; error: ApolloError }
  | AuthenticatedSession;

const isSandboxOrDev = import.meta.env.VITE_SANDBOX === 'true' || import.meta.env.MODE === 'development';

function updateFeatureFlags(flags: Partial<ServerSideFlags>) {
  if (!isSandboxOrDev) return;
  sessionStore.send({ type: 'setPlaygroundFlags', flags });
}

export function useSession(): AuthenticatedSession;
export function useSession(required: true): AuthenticatedSession;
export function useSession(required: false): UseSessionResult;
export function useSession(required = true): UseSessionResult {
  const ctx = useSelector(sessionStore, (s) => s.context);

  if (ctx.status === 'authenticated' && !isNil(ctx.user) && !isNil(ctx.org) && !isNil(ctx.featureFlags)) {
    const merged = ctx.playgroundFlags ? { ...ctx.featureFlags, ...ctx.playgroundFlags } : ctx.featureFlags;

    const hasFeatureWriteAccess = (feature: Features) => {
      const role = ctx.user!.role;
      const isAdmin = role === role_enum.ORG_ADMIN;

      const features = {
        energySupplier: {
          write: isAdmin || role === role_enum.BANKING_USER,
        },
        energyNetwork: {
          write: isAdmin || role === role_enum.BANKING_USER,
        },
      };

      return isAdmin || features[feature].write;
    };

    return {
      status: 'authenticated',
      user: ctx.user,
      org: ctx.org,
      featureFlags: merged,
      isAdmin: ctx.isAdmin,
      hasPortfolioWithWriteAccess: ctx.hasPortfolioWithWriteAccess,
      firstBuildingId: ctx.firstBuildingId,
      updateFeatureFlags,
      hasFeatureWriteAccess,
    };
  }

  if (required) {
    throw new Error(
      `useSession() requires an authenticated user, but status is '${ctx.status}'. Call useSession(false) to opt out.`,
    );
  }

  if (ctx.status === 'error' && !isNil(ctx.error)) {
    return { status: 'error', error: ctx.error };
  }

  if (ctx.status === 'unauthenticated') {
    return { status: 'unauthenticated' };
  }

  return { status: 'loading' };
}
